import createActivityDetector from 'activity-detector';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { LinkButton, StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { Link } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { EikaModal } from '../../components/EikaModal';
import { useConfig } from '../../defaultConfig';
import useTranslation from '../../hooks/useTranslation';
import checkKeepAlive from '../../services/keepAliveService';

import './KeepAlive.scss';

const activityDetector = createActivityDetector();
const KeepAlive: React.FC = () => {
    const { logoutLink, minutesUntilLogOutWarning, appContext } = useConfig();
    const translated = useTranslation();
    const timeout = useRef<ReturnType<typeof setInterval>>();
    const warningTimeout = useRef<ReturnType<typeof setInterval>>();
    const [isActive, setActive] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [showLogOutWarning, setSetShowLogoutWarning] = useState(false);

    activityDetector.on('idle', () => setActive(false));
    activityDetector.on('active', () => setActive(true));
    const checkAlive = useCallback(() => {
        checkKeepAlive(appContext).then(setIsLoggedIn);
    }, [appContext]);

    useEffect(() => {
        if (isActive) {
            checkAlive();
            clearTimeout(warningTimeout.current);
            timeout.current = setInterval(checkAlive, 10000);
        } else {
            clearInterval(timeout.current);
            warningTimeout.current = setTimeout(() => {
                setSetShowLogoutWarning(true);
            }, minutesUntilLogOutWarning * 60000);
        }
        return () => {
            clearInterval(timeout.current);
            clearTimeout(warningTimeout.current);
        };
    }, [checkAlive, isActive, minutesUntilLogOutWarning]);

    const logOutNative = (automaticLogout: boolean) => {
        postMessageRN({
            action: WebToRNAction.LOGOUT,
            payload: { automaticLogout },
        });
    };

    useEffect(() => {
        if (!isLoggedIn && runningInRNWebView) {
            logOutNative(true);
        } else if (!isLoggedIn && logoutLink.url) {
            window.location.href = logoutLink.url;
        }
    }, [isLoggedIn, logoutLink.url]);

    if (runningInRNWebView) {
        return <></>;
    }

    return (
        <EikaModal
            id="keep-alive-modal"
            setOpen={setSetShowLogoutWarning}
            open={showLogOutWarning}
            className="keep-alive-modal"
            containerClassName="keep-alive-modal__wrapper"
            shouldCloseOnOverlayClick
        >
            <div className="keep-alive">
                <InfoCard skipBackground>
                    <RichIcon type={RichIconTypes.ONLINE} />
                    <InfoCard.TitleH2>{translated.keepAlive.title}</InfoCard.TitleH2>
                    <p>{translated.keepAlive.description}</p>
                    <div className="keep-alive__button-row">
                        {runningInRNWebView ? (
                            <LinkButton onClick={() => logOutNative(false)}>{translated.logout}</LinkButton>
                        ) : (
                            <>{logoutLink && <Link href={logoutLink.url}>{translated.logout}</Link>} </>
                        )}
                        <StandardButton onClick={() => setSetShowLogoutWarning(false)}>
                            {translated.keepAlive.confirmationButtonText}
                        </StandardButton>
                    </div>
                </InfoCard>
            </div>
        </EikaModal>
    );
};
export default KeepAlive;
